<script>
import { mapActions } from 'vuex'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import HeightTransition from '@/components/general/height-transition.vue'
import baseMixin from '@/mixins/baseMixin'
import candidateMixin from '@/mixins/candidateMixin'
import Colours from '@/shared/constants/design/Colours'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import {
  CertChipLimits,
  TruncationCert,
} from '@/shared/constants/booking/BookingSelectEducators'

export default {
  name: 'BookableEducatorCard',
  components: { CandidateImage, HeightTransition },
  mixins: [baseMixin, candidateMixin],
  props: {
    educator: {
      type: Object,
      required: true,
    },
    totalScheduleDays: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      certifications: [],
      showPreferredIcon: false,
      hasLoadedDetails: false,
      isLoadingDetails: false,
      Colours,
      VuetifyTextClass,
    }
  },
  computed: {
    educatorTypes() {
      return (this.educator.candidateTypes ?? []).map((t) => t.title).join(', ')
    },
    daysAvailable() {
      return this.educator.availability?.noOfDaysAvailable ?? 0
    },
    isFullyAvailable() {
      return this.daysAvailable >= this.totalScheduleDays
    },
    availabilityBadgeClasses() {
      return this.isFullyAvailable
        ? [Colours.semanticsSuccess100, Colours.text.semanticsSuccess500]
        : [Colours.semanticsWarning200, Colours.text.semanticsWarning500]
    },
    certificationChipLimit() {
      return this.isLargeOrHigher
        ? CertChipLimits.isLargeOrHigher
        : CertChipLimits.isMediumOrLower
    },
    shouldTruncateCerts() {
      return this.certifications.length > this.certificationChipLimit
    },
    displayedCertifications() {
      if (!this.shouldTruncateCerts) return this.certifications

      return [
        ...this.certifications.slice(0, this.certificationChipLimit),
        TruncationCert,
      ]
    },
  },
  methods: {
    ...mapActions({
      loadCandidate: 'candidate/loadCandidate',
    }),
    onEducatorCardIntersect(entries, observer, isIntersecting) {
      if (!isIntersecting) return
      this.loadEducatorDetails()
    },
    async loadEducatorDetails() {
      if (this.isLoadingDetails || this.hasLoadedDetails) return

      this.isLoadingDetails = true

      const result = await this.loadCandidate({
        id: this.educator.id,
        silent: true,
      })

      if (result?.isSuccess) {
        this.certifications = result.data.certifications ?? []
        this.showPreferredIcon = (result.data.preferredLevels?.length ?? 0) > 0
      }

      this.isLoadingDetails = false
      this.hasLoadedDetails = true
    },
  },
}
</script>

<template>
  <div
    v-intersect.once="{
      handler: onEducatorCardIntersect,
      options: { rootMargin: '100px' },
    }"
  >
    <v-card
      class="rounded-lg pa-4"
      outlined
      elevation="0"
      data-testid="bookable-educator-card"
    >
      <div class="d-flex align-center">
        <div class="d-flex flex-column flex-grow-1 min-width-0 overflow-hidden">
          <div class="d-flex min-width-0 overflow-hidden">
            <v-lazy>
              <CandidateImage
                :id="educator.id"
                :full-name="educator.fullName"
                size="72"
                :font-size="22"
                margin="mr-4"
                rounded
                :show-preferred-icon="showPreferredIcon"
              />
            </v-lazy>

            <div class="flex-grow-1 min-width-0 overflow-hidden">
              <div class="d-flex align-center flex-wrap">
                <h4
                  class="display-xs ma-0 text-truncate"
                  data-testid="educator-name"
                >
                  {{ educator.fullName }}
                </h4>
                <span
                  class="d-inline-flex align-center text-caption-bold rounded-pill px-2 py-1 ml-2 flex-shrink-0 text-no-wrap"
                  :class="availabilityBadgeClasses"
                >
                  {{
                    isFullyAvailable
                      ? $t('bookingCreateV2.selectEducatorsStep.availableBadge')
                      : $t(
                          'bookingCreateV2.selectEducatorsStep.availablePartialBadge',
                          [daysAvailable, totalScheduleDays]
                        )
                  }}
                </span>
              </div>

              <div
                v-if="educatorTypes"
                class="text-sm mt-1 text-truncate"
                data-testid="educator-types"
              >
                {{ educatorTypes }}
              </div>

              <div class="text-sm mt-1">
                {{
                  $t('bookingCreateV2.selectEducatorsStep.lastBookedLabel', [
                    educator.lastBookedDate
                      ? displayDate(educator.lastBookedDate)
                      : $t(
                          'bookingCreateV2.selectEducatorsStep.lastBookedNever'
                        ),
                  ])
                }}
              </div>
            </div>
          </div>

          <HeightTransition class="mt-2" content-transition="fade">
            <div
              v-if="!hasLoadedDetails"
              key="loading"
              class="d-flex flex-wrap"
            >
              <v-skeleton-loader
                v-for="n in 3"
                :key="n"
                loading
                type="chip"
                class="educator-card-skeleton-chip ma-0 mr-2 rounded-pill"
              />
            </div>

            <div
              v-else-if="certifications.length"
              key="certifications"
              class="d-flex flex-wrap"
            >
              <span
                v-for="cert in displayedCertifications"
                :key="cert.certId"
                class="d-inline-flex align-center text-caption-bold rounded-pill px-2 py-1 mr-2 mb-1 text-no-wrap"
                :class="[Colours.neutralsGrey50, Colours.text.neutralsGrey700]"
              >
                {{ cert.typeName }}
              </span>
            </div>

            <div v-else key="empty" class="d-flex align-center">
              <span class="text-caption-bold opacity-50 px-2 py-1 mb-1">
                {{
                  $t(
                    'bookingCreateV2.selectEducatorsStep.noCertificationsFound'
                  )
                }}
              </span>
            </div>
          </HeightTransition>
        </div>

        <div class="flex-shrink-0 ml-4">
          <v-btn
            outlined
            class="pa-3 text-md-bold border-grey-200"
            :disabled="!isFullyAvailable"
            data-testid="educator-add-btn"
            :aria-label="
              $t('bookingCreateV2.selectEducatorsStep.addBtn') +
                ' ' +
                educator.fullName
            "
            @click="$emit('add', educator)"
          >
            <v-icon small left>mdi-plus</v-icon>
            {{ $t('bookingCreateV2.selectEducatorsStep.addBtn') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<style lang="scss" src="@/styles/candidate/candidate-card.scss"></style>

<style lang="scss" scoped>
@import '@/styles/_vue-transitions.scss';

.educator-card-skeleton-chip {
  height: 30px;
}
</style>
